import { Component, ElementRef, NgZone, OnDestroy, OnInit } from '@angular/core';
import { createRoot, Root } from 'react-dom/client';
import * as React from 'react';
import { of, Subscription } from 'rxjs';
import { catchError, take } from 'rxjs/operators';

import { AuthService } from '@app/data/services/authService';
import { Site, SitesService } from '@app/data/services/sitesService';

const MapViewSiteSelector = require('@react/components/mapview/MapViewSiteSelector').default;

/**
 * React StreamMAP site selector for the StreamDASH drawer.
 *
 * The selector is mounted outside the drawer's scroll container so its dropdown
 * is not clipped. Angular keeps ownership of the available sites and the
 * selected-site service.
 */
@Component({
  selector: 'app-dashboard-site-selector-bridge',
  template: `
    <div class="stream-dashboard-site-selector">
      <div class="dashboard-site-selector-root"></div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }

      .dashboard-site-selector-root {
        display: block;
      }
    `,
  ],
})
export class DashboardSiteSelectorBridgeComponent implements OnInit, OnDestroy {
  private reactRoot: Root | null = null;
  private sitesSubscription: Subscription | null = null;
  private selectedSiteSubscription: Subscription | null = null;
  private siteRecords: Site[] = [];
  private selectedSiteId: string | null = null;

  constructor(
    private el: ElementRef<HTMLElement>,
    private authService: AuthService,
    private sitesService: SitesService,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    const container = this.el.nativeElement.querySelector('.dashboard-site-selector-root');
    if (!container) return;

    this.reactRoot = createRoot(container);

    this.selectedSiteSubscription = this.sitesService.getSelectedSite().subscribe((site) => {
      this.selectedSiteId = site?.id ?? null;
      this.renderSelector();
    });

    this.sitesSubscription = this.authService
      .getLoggedInUser()
      .pipe(
        take(1),
        catchError(() => of({ user: null, sites: [] as Site[], forbidden: false }))
      )
      .subscribe(({ sites }) => {
        this.siteRecords = sites || [];

        if (!this.selectedSiteId && this.siteRecords.length) {
          this.sitesService.setSelectedSite(this.siteRecords[0]);
        }

        this.renderSelector();
      });
  }

  ngOnDestroy(): void {
    this.sitesSubscription?.unsubscribe();
    this.selectedSiteSubscription?.unsubscribe();
    this.reactRoot?.unmount();
    this.reactRoot = null;
  }

  private renderSelector(): void {
    if (!this.reactRoot) return;

    this.reactRoot.render(
      React.createElement(MapViewSiteSelector, {
        sites: this.siteRecords.map((site) => ({ id: site.id, name: site.name })),
        selectedSiteId: this.selectedSiteId,
        onSelectSite: (siteId: string) =>
          this.ngZone.run(() => {
            const selected = this.siteRecords.find((site) => site.id === siteId);
            if (selected) {
              this.sitesService.setSelectedSite(selected);
            }
          }),
      })
    );
  }
}
